import { useIntl } from 'react-intl'
import Pane from 'components/Common/Pane'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import useSaleUnitFormatting from 'hooks/useSaleUnitFormatting'
import type { OfferPageMultiRoomAutoselectionReason, OfferPageMultiRoomMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MultiRoomModeToggle from '../MultiRoomModeToggle'
import PreSelectionBanner from '../PreSelectionBanner'
import InvalidSelectionBanner from '../InvalidSelectionBanner'
import PriceModeSelectMobile from 'components/OfferPage/Filters/PriceModeSelect/PriceModeSelectMobile'
import GrandHyattBaliFutureDatesBanner from 'components/OfferPage/FlashHotel/GrandHyattBaliFutureDatesBanner'
import Group from 'components/utils/Group'

export const ROOM_MODE_TOGGLE_SECTION_ID = 'roomModeToggleSection'

interface Props {
  roomMode?: OfferPageMultiRoomMode
  onModeChange?: (mode: OfferPageMultiRoomMode) => void
  rooms: Array<OfferPageRoom>
  saleUnit: string
  autoselectionReason?: OfferPageMultiRoomAutoselectionReason
  charterBundleRoute?: App.CharterBundleRoute
  withHeading?: boolean
  showGrandHyattBaliFutureDatesBanner?: boolean
}

function OfferPageRoomModeToggleSection({
  roomMode = 'individual',
  onModeChange,
  rooms,
  saleUnit,
  autoselectionReason,
  charterBundleRoute,
  withHeading = true,
  showGrandHyattBaliFutureDatesBanner = false,
}: Props) {
  const intl = useIntl()
  const { formatSaleUnitCount, formatSaleUnitLabel, getSaleUnitPluralNoun } = useSaleUnitFormatting()
  const roomsAreSelected = rooms.some(room => room.roomId)
  const showPreSelectionBanner = roomsAreSelected && roomMode === 'individual' && autoselectionReason === 'initial-selection'
  const showInvalidSelectionBanner = roomsAreSelected && roomMode === 'individual' && autoselectionReason === 'invalid-selections'
  const localizedSaleUnit = formatSaleUnitLabel(saleUnit)

  return <Pane padding="20 20 16" id={ROOM_MODE_TOGGLE_SECTION_ID}>
    <Group direction="vertical" gap={16}>
      <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" noDisplayWhenEmpty>
        {withHeading && <Heading variant="heading4">
          {intl.formatMessage(
            {
              id: '58ORFaH3LbM1P88spdx1',
              defaultMessage: 'Select your {saleUnitLabel}',
              description: 'Heading for room selection section with sale unit type and count',
            },
            { saleUnitLabel: formatSaleUnitCount(saleUnit, rooms.length) },
          )}
        </Heading>}
        {!!charterBundleRoute && <PriceModeSelectMobile />}
      </Group>
      <VerticalSpacer gap={12}>
        <BodyText variant="medium">
          {intl.formatMessage(
            {
              id: 'ix1Rio3ItBsrXIHcx9wb',
              defaultMessage: 'Choose the same package for all {pluralSaleUnit}, or customise each {saleUnit} individually.',
              description: 'Description for room mode toggle explaining individual vs same package selection',
            },
            {
              pluralSaleUnit: getSaleUnitPluralNoun(saleUnit),
              saleUnit: localizedSaleUnit,
            },
          )}
        </BodyText>
        <MultiRoomModeToggle inputsName="room-mode-mobile" onModeChange={onModeChange} />
      </VerticalSpacer>
      {showGrandHyattBaliFutureDatesBanner && <GrandHyattBaliFutureDatesBanner />}
      {showPreSelectionBanner && <PreSelectionBanner saleUnit={saleUnit} />}
      {showInvalidSelectionBanner && <InvalidSelectionBanner saleUnit={saleUnit} />}
    </Group>
  </Pane>
}

export default OfferPageRoomModeToggleSection
